import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { useNavigate, useParams } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    ArrowLeft, Building2, Mail, Phone, Globe, FileText,
    CheckCircle, XCircle, Clock, Key, Shield, Copy,
    Calendar, User, Trash2, AlertTriangle, X,
} from 'lucide-react';

function PortalAccountDetails() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [account, setAccount] = React.useState<any>(null);

    // Modals
    const [showApproveModal, setShowApproveModal] = React.useState<boolean>(false);
    const [showRejectModal, setShowRejectModal] = React.useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
    const [showKeyModal, setShowKeyModal] = React.useState<any>(null);
    const [actionLoader, setActionLoader] = React.useState<boolean>(false);

    // Forms
    const [approveForm, setApproveForm] = React.useState({ rate_limit_per_minute: 120 });
    const [rejectReason, setRejectReason] = React.useState<string>('');

    React.useEffect(() => {
        if (id) loadAccount();
    }, [id]);

    const loadAccount = () => {
        setLoading(true);
        ApiGatewayApis.getSinglePortalAccount(parseInt(id!))
            .then((response: any) => {
                setLoading(false);
                if (response?.data?.data) {
                    setAccount(response.data.data);
                }
            })
            .catch((error: any) => {
                setLoading(false);
                toast.error('Failed to load account details');
            });
    };

    const handleApprove = (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        setActionLoader(true);

        ApiGatewayApis.approvePortalAccount(parseInt(id), {
            rate_limit_per_minute: approveForm.rate_limit_per_minute,
        })
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('Account approved! API key generated and sent via email.');
                    setShowApproveModal(false);

                    const apiKey = response.data.data?.api_key;
                    const apiSecret = response.data.data?.api_secret;
                    if (apiKey) {
                        setShowKeyModal({ key: apiKey, secret: apiSecret });
                    }
                    loadAccount();
                } else {
                    toast.warn(response?.data?.message || 'Failed to approve');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error approving account');
            });
    };

    const handleReject = (e: React.FormEvent) => {
        e.preventDefault();
        if (!id || !rejectReason.trim()) return;
        setActionLoader(true);

        ApiGatewayApis.rejectPortalAccount(parseInt(id), { reason: rejectReason })
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('Account rejected. Notification sent.');
                    setShowRejectModal(false);
                    setRejectReason('');
                    loadAccount();
                } else {
                    toast.warn(response?.data?.message || 'Failed to reject');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error rejecting account');
            });
    };

    const handleDelete = () => {
        if (!id) return;
        setActionLoader(true);

        ApiGatewayApis.deletePortalAccount(parseInt(id))
            .then((response: any) => {
                setActionLoader(false);
                if (response?.data?.success) {
                    toast.success('Account deleted');
                    navigate('/admin/api-gateway/portal-accounts');
                } else {
                    toast.warn(response?.data?.message || 'Failed to delete');
                }
            })
            .catch((error: any) => {
                setActionLoader(false);
                toast.error(error?.response?.data?.message || 'Error deleting account');
            });
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            case 'suspended': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'active': return <CheckCircle size={16} className='text-green-600' />;
            case 'pending': return <Clock size={16} className='text-yellow-600' />;
            case 'rejected': return <XCircle size={16} className='text-red-600' />;
            default: return <Clock size={16} className='text-gray-400' />;
        }
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    };

    return (
        <>
            <AdminSidebar title="Portal Account" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Portal Account Details" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>

                    {/* Back + Header */}
                    <div className='flex items-center gap-3 mb-5'>
                        <button onClick={() => navigate('/admin/api-gateway/portal-accounts')} className='text-gray-500 hover:text-[#E04343]'>
                            <ArrowLeft size={20} />
                        </button>
                        <h1 className='text-[20px] font-bold text-[#263238]'>Portal Account Details</h1>
                    </div>

                    {loading ? (
                        <div className='flex justify-center py-20'>
                            <Oval visible={true} height="35" width="35" color="#E04343" secondaryColor="#FCBF94" />
                        </div>
                    ) : !account ? (
                        <div className='text-center py-20'>
                            <p className='text-[#808080]'>Account not found</p>
                        </div>
                    ) : (
                        <div className='grid lg:grid-cols-3 gap-5'>

                            {/* ========== LEFT: Account Info ========== */}
                            <div className='lg:col-span-2 space-y-5'>

                                {/* Pending Banner */}
                                {account.status === 'pending' && (
                                    <div className='bg-yellow-50 border border-yellow-200 rounded-[10px] p-4 flex items-start gap-3'>
                                        <Clock size={20} className='text-yellow-600 mt-0.5 flex-shrink-0' />
                                        <div>
                                            <p className='text-[13px] font-semibold text-yellow-800'>Pending Approval</p>
                                            <p className='text-[12px] text-yellow-700 mt-1'>
                                                This account is waiting for admin review. Approve to create a Partner record and generate API credentials, or reject with a reason.
                                            </p>
                                            <div className='flex gap-2 mt-3'>
                                                <button
                                                    onClick={() => setShowApproveModal(true)}
                                                    className='px-4 py-1.5 text-sm bg-green-600 text-white rounded-[5px] hover:bg-green-700 flex items-center gap-1.5'
                                                >
                                                    <CheckCircle size={14} /> Approve
                                                </button>
                                                <button
                                                    onClick={() => setShowRejectModal(true)}
                                                    className='px-4 py-1.5 text-sm bg-red-600 text-white rounded-[5px] hover:bg-red-700 flex items-center gap-1.5'
                                                >
                                                    <XCircle size={14} /> Reject
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Rejection Banner */}
                                {account.status === 'rejected' && (
                                    <div className='bg-red-50 border border-red-200 rounded-[10px] p-4'>
                                        <p className='text-[13px] font-semibold text-red-800 flex items-center gap-2'>
                                            <XCircle size={16} /> Application Rejected
                                        </p>
                                        {account.rejection_reason && (
                                            <p className='text-[12px] text-red-700 mt-1'>
                                                <strong>Reason:</strong> {account.rejection_reason}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Basic Info Card */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-semibold text-[#263238] mb-4 flex items-center gap-2'>
                                        <Building2 size={16} className='text-[#E04343]' />
                                        Company Information
                                    </h3>
                                    <div className='grid md:grid-cols-2 gap-x-6 gap-y-3'>
                                        <div>
                                            <label className='text-[11px] font-semibold text-[#808080] uppercase'>Company Name</label>
                                            <p className='text-[13px] text-[#263238] mt-0.5'>{account.company_name}</p>
                                        </div>
                                        <div>
                                            <label className='text-[11px] font-semibold text-[#808080] uppercase'>Account Type</label>
                                            <p className='mt-0.5'>
                                                <span className={`text-[11px] font-semibold px-2 py-[3px] rounded-full capitalize ${account.account_type === 'partner' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                                                    }`}>
                                                    {account.account_type}
                                                </span>
                                            </p>
                                        </div>
                                        <div>
                                            <label className='text-[11px] font-semibold text-[#808080] uppercase'>Contact Name</label>
                                            <p className='text-[13px] text-[#263238] mt-0.5 flex items-center gap-1.5'>
                                                <User size={13} className='text-gray-400' /> {account.contact_name}
                                            </p>
                                        </div>
                                        <div>
                                            <label className='text-[11px] font-semibold text-[#808080] uppercase'>Contact Email</label>
                                            <p className='text-[13px] text-[#263238] mt-0.5 flex items-center gap-1.5'>
                                                <Mail size={13} className='text-gray-400' /> {account.contact_email}
                                            </p>
                                        </div>
                                        <div>
                                            <label className='text-[11px] font-semibold text-[#808080] uppercase'>Phone Number</label>
                                            <p className='text-[13px] text-[#263238] mt-0.5 flex items-center gap-1.5'>
                                                <Phone size={13} className='text-gray-400' /> {account.phone_number || '—'}
                                            </p>
                                        </div>
                                        <div>
                                            <label className='text-[11px] font-semibold text-[#808080] uppercase'>Website</label>
                                            <p className='text-[13px] text-[#263238] mt-0.5 flex items-center gap-1.5'>
                                                <Globe size={13} className='text-gray-400' />
                                                {account.website ? (
                                                    <a href={account.website} target='_blank' rel='noopener noreferrer' className='text-[#E04343] hover:underline'>
                                                        {account.website}
                                                    </a>
                                                ) : '—'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Business Description */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-semibold text-[#263238] mb-3 flex items-center gap-2'>
                                        <FileText size={16} className='text-[#E04343]' />
                                        Business Description
                                    </h3>
                                    <p className='text-[13px] text-[#263238] leading-relaxed whitespace-pre-line'>
                                        {account.business_description || 'No description provided.'}
                                    </p>
                                    {account.expected_monthly_volume && (
                                        <div className='mt-3 pt-3 border-t border-gray-100'>
                                            <label className='text-[11px] font-semibold text-[#808080] uppercase'>Expected Monthly Volume</label>
                                            <p className='text-[13px] text-[#263238] mt-0.5'>{account.expected_monthly_volume}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Linked API Keys (if approved) */}
                                {account.api_keys && account.api_keys.length > 0 && (
                                    <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                        <h3 className='text-[14px] font-semibold text-[#263238] mb-3 flex items-center gap-2'>
                                            <Key size={16} className='text-[#E04343]' />
                                            API Keys
                                        </h3>
                                        <div className='space-y-2'>
                                            {account.api_keys.map((key: any) => (
                                                <div key={key.id} className='flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3'>
                                                    <div className='flex items-center gap-3'>
                                                        <code className='text-[12px] font-mono bg-[#1a1a2e] text-[#4ade80] px-2 py-0.5 rounded'>
                                                            {key.key_prefix}
                                                        </code>
                                                        <span className='text-[12px] text-[#808080]'>
                                                            {key.type} · {key.total_requests?.toLocaleString() || 0} requests
                                                        </span>
                                                    </div>
                                                    <span className={`text-[11px] font-semibold px-2 py-[3px] rounded-full ${key.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {key.is_active ? 'Active' : 'Revoked'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ========== RIGHT: Status & Meta ========== */}
                            <div className='space-y-5'>

                                {/* Status Card */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-semibold text-[#263238] mb-4 flex items-center gap-2'>
                                        <Shield size={16} className='text-[#E04343]' />
                                        Status
                                    </h3>
                                    <div className='flex items-center gap-2 mb-4'>
                                        {getStatusIcon(account.status)}
                                        <span className={`text-[12px] font-semibold px-2.5 py-1 rounded-full capitalize ${getStatusBadge(account.status)}`}>
                                            {account.status}
                                        </span>
                                    </div>
                                    <div className='space-y-3 text-[13px]'>
                                        <div className='flex justify-between'>
                                            <span className='text-[#808080]'>Applied</span>
                                            <span className='text-[#263238]'>{formatDate(account.created_at)}</span>
                                        </div>
                                        {account.approved_at && (
                                            <div className='flex justify-between'>
                                                <span className='text-[#808080]'>Approved</span>
                                                <span className='text-[#263238]'>{formatDate(account.approved_at)}</span>
                                            </div>
                                        )}
                                        {account.approved_by && (
                                            <div className='flex justify-between'>
                                                <span className='text-[#808080]'>Approved By</span>
                                                <span className='text-[#263238]'>{account.approved_by}</span>
                                            </div>
                                        )}
                                        {account.last_login_at && (
                                            <div className='flex justify-between'>
                                                <span className='text-[#808080]'>Last Login</span>
                                                <span className='text-[#263238]'>{formatDate(account.last_login_at)}</span>
                                            </div>
                                        )}
                                        {account.partner_id && (
                                            <div className='flex justify-between'>
                                                <span className='text-[#808080]'>Partner ID</span>
                                                <button
                                                    onClick={() => navigate(`/admin/api-gateway/partners/${account.partner_id}`)}
                                                    className='text-[#E04343] hover:underline'
                                                >
                                                    #{account.partner_id}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Actions Card */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-semibold text-[#263238] mb-4'>Actions</h3>
                                    <div className='space-y-2'>
                                        {account.status === 'pending' && (
                                            <>
                                                <button
                                                    onClick={() => setShowApproveModal(true)}
                                                    className='w-full flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg px-4 py-2.5 text-[13px] transition'
                                                >
                                                    <CheckCircle size={16} /> Approve Account
                                                </button>
                                                <button
                                                    onClick={() => setShowRejectModal(true)}
                                                    className='w-full flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg px-4 py-2.5 text-[13px] transition'
                                                >
                                                    <XCircle size={16} /> Reject Account
                                                </button>
                                            </>
                                        )}
                                        {account.status === 'active' && account.partner_id && (
                                            <button
                                                onClick={() => navigate(`/admin/api-gateway/partners/${account.partner_id}`)}
                                                className='w-full flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg px-4 py-2.5 text-[13px] transition'
                                            >
                                                <Building2 size={16} /> View Partner Record
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setShowDeleteModal(true)}
                                            className='w-full flex items-center gap-2 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg px-4 py-2.5 text-[13px] transition'
                                        >
                                            <Trash2 size={16} /> Delete Account
                                        </button>
                                    </div>
                                </div>

                                {/* Account ID Card */}
                                <div className='bg-white shadow-sm rounded-[10px] p-5'>
                                    <h3 className='text-[14px] font-semibold text-[#263238] mb-3'>Technical</h3>
                                    <div className='space-y-2 text-[13px]'>
                                        <div className='flex justify-between'>
                                            <span className='text-[#808080]'>Account ID</span>
                                            <span className='text-[#263238] font-mono'>#{account.id}</span>
                                        </div>
                                        <div className='flex justify-between'>
                                            <span className='text-[#808080]'>Portal Account</span>
                                            <span className='text-[#263238] font-mono text-[11px]'>api_portal_accounts</span>
                                        </div>
                                        {account.last_login_ip && (
                                            <div className='flex justify-between'>
                                                <span className='text-[#808080]'>Last IP</span>
                                                <span className='text-[#263238] font-mono'>{account.last_login_ip}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ========== APPROVE MODAL ========== */}
            {showApproveModal && account && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[480px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                            <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                <CheckCircle size={18} className='text-green-600' />
                                Approve Account
                            </h3>
                            <button onClick={() => setShowApproveModal(false)} className='text-gray-400 hover:text-gray-600'>
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={handleApprove}>
                            <div className='p-5 space-y-4'>
                                <div className='bg-green-50 rounded-lg p-3 border border-green-100'>
                                    <p className='text-[13px] text-green-800'>
                                        Approving <strong>{account.company_name}</strong> ({account.account_type})
                                    </p>
                                    <p className='text-[11px] text-green-700 mt-1'>
                                        This will create a Partner record, generate API key + secret, and email the credentials.
                                    </p>
                                </div>
                                <div>
                                    <label className='text-[12px] font-medium text-[#263238] block mb-1'>Rate Limit (requests/minute)</label>
                                    <input
                                        type='number'
                                        value={approveForm.rate_limit_per_minute}
                                        onChange={(e) => setApproveForm({ ...approveForm, rate_limit_per_minute: parseInt(e.target.value) || 120 })}
                                        min={10} max={10000}
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2 w-full'
                                    />
                                </div>
                            </div>
                            <div className='px-5 py-3 border-t border-gray-100 flex gap-2 justify-end'>
                                <button type='button' onClick={() => setShowApproveModal(false)} className='px-4 py-2 text-sm border border-[#DADADA] rounded-[5px] hover:bg-gray-50'>
                                    Cancel
                                </button>
                                <button type='submit' disabled={actionLoader}
                                    className='px-4 py-2 text-sm bg-green-600 text-white rounded-[5px] hover:bg-green-700 disabled:opacity-50 flex items-center gap-2'>
                                    {actionLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                    Approve & Generate Key
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========== REJECT MODAL ========== */}
            {showRejectModal && account && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[480px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                            <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                <XCircle size={18} className='text-red-600' />
                                Reject Application
                            </h3>
                            <button onClick={() => setShowRejectModal(false)} className='text-gray-400 hover:text-gray-600'>
                                <X size={18} />
                            </button>
                        </div>
                        <form onSubmit={handleReject}>
                            <div className='p-5 space-y-4'>
                                <div className='bg-red-50 rounded-lg p-3 border border-red-100'>
                                    <p className='text-[13px] text-red-800'>
                                        Rejecting <strong>{account.company_name}</strong>. The applicant will be notified via email.
                                    </p>
                                </div>
                                <div>
                                    <label className='text-[12px] font-medium text-[#263238] block mb-1'>Rejection Reason <span className='text-red-500'>*</span></label>
                                    <textarea
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        placeholder='Provide a reason for rejection...'
                                        rows={3} required
                                        className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2 w-full resize-none'
                                    />
                                </div>
                            </div>
                            <div className='px-5 py-3 border-t border-gray-100 flex gap-2 justify-end'>
                                <button type='button' onClick={() => setShowRejectModal(false)} className='px-4 py-2 text-sm border border-[#DADADA] rounded-[5px] hover:bg-gray-50'>
                                    Cancel
                                </button>
                                <button type='submit' disabled={actionLoader || !rejectReason.trim()}
                                    className='px-4 py-2 text-sm bg-red-600 text-white rounded-[5px] hover:bg-red-700 disabled:opacity-50 flex items-center gap-2'>
                                    {actionLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                    Reject & Notify
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ========== DELETE CONFIRMATION MODAL ========== */}
            {showDeleteModal && account && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[400px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                            <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                <AlertTriangle size={18} className='text-red-600' />
                                Confirm Delete
                            </h3>
                            <button onClick={() => setShowDeleteModal(false)} className='text-gray-400 hover:text-gray-600'>
                                <X size={18} />
                            </button>
                        </div>
                        <div className='p-5'>
                            <p className='text-[13px] text-[#263238]'>
                                Are you sure you want to delete the portal account for <strong>{account.company_name}</strong>?
                                This is a soft delete and can be reversed.
                            </p>
                        </div>
                        <div className='px-5 py-3 border-t border-gray-100 flex gap-2 justify-end'>
                            <button onClick={() => setShowDeleteModal(false)} className='px-4 py-2 text-sm border border-[#DADADA] rounded-[5px] hover:bg-gray-50'>
                                Cancel
                            </button>
                            <button onClick={handleDelete} disabled={actionLoader}
                                className='px-4 py-2 text-sm bg-red-600 text-white rounded-[5px] hover:bg-red-700 disabled:opacity-50 flex items-center gap-2'>
                                {actionLoader && <Oval visible={true} height="14" width="14" color="#fff" secondaryColor="#fff" />}
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ========== API KEY REVEAL MODAL ========== */}
            {showKeyModal && (
                <div className='fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4'>
                    <div className='bg-white rounded-[10px] w-full max-w-[520px] shadow-xl'>
                        <div className='flex justify-between items-center px-5 py-4 border-b border-gray-100'>
                            <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                <Key size={18} className='text-[#E04343]' />
                                Generated API Credentials
                            </h3>
                            <button onClick={() => setShowKeyModal(null)} className='text-gray-400 hover:text-gray-600'>
                                <X size={18} />
                            </button>
                        </div>
                        <div className='p-5 space-y-4'>
                            <div className='bg-yellow-50 border border-yellow-200 rounded-lg p-3'>
                                <p className='text-[12px] text-yellow-800 font-medium'>
                                    These credentials are shown ONCE and have been sent to the partner's email. Copy now if needed.
                                </p>
                            </div>
                            <div>
                                <label className='text-[11px] font-semibold text-[#808080] uppercase block mb-1'>API Key</label>
                                <div className='flex items-center gap-2'>
                                    <code className='flex-1 bg-[#1a1a2e] text-[#4ade80] px-3 py-2 rounded text-[12px] font-mono break-all'>
                                        {showKeyModal.key}
                                    </code>
                                    <button onClick={() => copyToClipboard(showKeyModal.key)} className='p-2 hover:bg-gray-100 rounded'>
                                        <Copy size={16} className='text-gray-500' />
                                    </button>
                                </div>
                            </div>
                            {showKeyModal.secret && (
                                <div>
                                    <label className='text-[11px] font-semibold text-[#808080] uppercase block mb-1'>API Secret</label>
                                    <div className='flex items-center gap-2'>
                                        <code className='flex-1 bg-[#1a1a2e] text-[#60a5fa] px-3 py-2 rounded text-[12px] font-mono break-all'>
                                            {showKeyModal.secret}
                                        </code>
                                        <button onClick={() => copyToClipboard(showKeyModal.secret)} className='p-2 hover:bg-gray-100 rounded'>
                                            <Copy size={16} className='text-gray-500' />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='px-5 py-3 border-t border-gray-100 flex justify-end'>
                            <button onClick={() => setShowKeyModal(null)} className='px-4 py-2 text-sm bg-[#E04343] text-white rounded-[5px] hover:bg-[#C93636]'>
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default PortalAccountDetails;